'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, User, X, ChevronDown, Eye, EyeOff, Mail, Lock, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { authAPI, propertyTypeAPI } from '@/lib/api';
import { Label } from '@/components/ui/label';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHostDialogOpen, setIsHostDialogOpen] = useState(false);
  const [hostStep, setHostStep] = useState<0 | 1 | 2>(0);
  const [selectedOption, setSelectedOption] = useState<'property' | 'service' | ''>('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [hostAuthMode, setHostAuthMode] = useState<'signin' | 'signup'>('signin');
  const [hostEmail, setHostEmail] = useState('');
  const [hostPassword, setHostPassword] = useState('');
  const [hostConfirmPassword, setHostConfirmPassword] = useState('');
  const [hostName, setHostName] = useState('');
  const [hostMobile, setHostMobile] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hostFlowError, setHostFlowError] = useState('');
  const [isHostAuthenticated, setIsHostAuthenticated] = useState(false);
  const [user, setUser] = useState<{ name?: string; email?: string } | null>(null);
  const [propertyOptions, setPropertyOptions] = useState<Array<{ _id?: string; id?: string; name: string; logo?: string; description?: string }>>([]);

  const pathname = usePathname();
  const router = useRouter();
  const isLandlordPage = pathname === '/landlord';
  const isGuestPage = pathname === '/guest';
  const isFurnishingPackagesPage = pathname === '/furnishing-packages';
  const isContactPage = pathname === '/contact';
  const isListingsPage = pathname === '/listings' || pathname.startsWith('/listings');
  const isAboutPage = pathname === '/about';
  const isDetailsPage = pathname.startsWith('/details/') || pathname.startsWith('/details');
  const isServicesPage = pathname.startsWith('/services/') || pathname.startsWith('/services');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsHostAuthenticated(authAPI.isAuthenticated());
  }, []);

  useEffect(() => {
    if (isHostAuthenticated) {
      const currentUser = authAPI.getCurrentUser();
      if (currentUser) {
        setUser({
          name: currentUser.name || currentUser.firstName || currentUser.username || 'User',
          email: currentUser.email || currentUser.login || ''
        });
      }
    } else {
      setUser(null);
    }
  }, [isHostAuthenticated]);

  const handleLogout = () => {
    authAPI.signOut();
    setIsHostAuthenticated(false);
    setUser(null);
    router.push('/');
  };

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const resp = await propertyTypeAPI.getAllWeb('property_type');
        const raw = (resp as any)?.data?.data;
        const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[];
        const formattedOptions = Array.isArray(list) ? list.map((item: any) => ({
          value: item._id || item.id,
          title: item.name,
          description: item.description || `${item.name} property type`
        })) : [];
        setPropertyOptions(raw);
        console.log(raw);
      } catch (error) {
        console.error('Error fetching property types:', error);
        // Fallback to hardcoded options if API fails
        setPropertyOptions([
          { _id: 'villa-1', name: 'Villa', description: 'Spacious stay with premium amenities.' },
          { _id: 'apartment-1', name: 'Apartment', description: 'City living with modern comforts.' },
          { _id: 'beach-house-1', name: 'Beach House', description: 'Waterfront homes for serene stays.' },
        ]);
      }
    };
    fetchPropertyTypes();
  }, []);

  const resetHostFlow = () => {
    setHostStep(0);
    setSelectedOption('');
    setSelectedPropertyType('');
    setHostAuthMode('signin');
    setHostEmail('');
    setHostPassword('');
    setHostConfirmPassword('');
    setHostName('');
    setHostMobile('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsLoading(false);
    setHostFlowError('');
  };

  const handleHostDialogChange = (open: boolean) => {
    setIsHostDialogOpen(open);
    if (!open) {
      resetHostFlow();
    } else {
      setIsHostAuthenticated(authAPI.isAuthenticated());
    }
  };

  const handleHostButtonClick = () => {
    handleHostDialogChange(true);
  };

  const handlePropertyTypeContinue = () => {
    if (!selectedPropertyType) {
      setHostFlowError('Please select a property type to continue.');
      return;
    }

    if (isHostAuthenticated) {
      router.push(`/admin/properties/add?propertyType=${encodeURIComponent(selectedPropertyType)}`);
      handleHostDialogChange(false);
      return;
    }

    setHostFlowError('');
    setHostStep(2);
  };

  const handleServiceContinue = () => {
    if (isHostAuthenticated) {
      router.push('/admin/property-services/add');
      handleHostDialogChange(false);
      return;
    }

    setHostStep(2);
  };

  const handleHostSignIn = async () => {
    if (!hostEmail.trim() || !hostPassword.trim()) {
      setHostFlowError('Please enter both email and password.');
      return;
    }

    setHostFlowError('');
    setIsLoading(true);

    try {
      const result = await authAPI.hostSignIn({
        login: hostEmail,
        password: hostPassword,
      });

      if (result.success) {
        setIsHostAuthenticated(true);
        const currentUser = authAPI.getCurrentUser();
        if (currentUser) {
          setUser({
            name: currentUser.name || currentUser.firstName || currentUser.username || 'Host',
            email: currentUser.email || currentUser.login || ''
          });
        }
        const redirectUrl = selectedOption === 'service'
          ? '/admin/property-services/add'
          : `/admin/properties/add?propertyType=${encodeURIComponent(selectedPropertyType)}`;
        router.push(redirectUrl);
        handleHostDialogChange(false);
      } else {
        setHostFlowError(result.message || 'Sign in failed. Please try again.');
      }
    } catch (error) {
      setHostFlowError('An error occurred during sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHostSignUp = async () => {
    if (!hostName.trim() || !hostEmail.trim() || !hostMobile.trim() || !hostPassword.trim()) {
      setHostFlowError('Please fill in all required fields.');
      return;
    }

    if (hostPassword !== hostConfirmPassword) {
      setHostFlowError('Passwords do not match.');
      return;
    }

    if (hostPassword.length < 6) {
      setHostFlowError('Password must be at least 6 characters long.');
      return;
    }

    setHostFlowError('');
    setIsLoading(true);

    try {
      const result = await authAPI.hostSignUp({
        name: hostName,
        email: hostEmail,
        mobile_number: hostMobile,
        password: hostPassword,
        confirm_password: hostConfirmPassword,
      });

      if (result.success) {
        setHostFlowError('');
        setHostAuthMode('signin');
        // Show success message and switch to signin
        setHostFlowError('Registration successful! Please sign in with your credentials.');
        setTimeout(() => setHostFlowError(''), 3000);
      } else {
        setHostFlowError(result.message || 'Sign up failed. Please try again.');
      }
    } catch (error) {
      setHostFlowError('An error occurred during sign up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-300 ${(!scrolled && !isListingsPage && !isDetailsPage && !isServicesPage) ? 'bg-transparent' : 'bg-white/95 backdrop-blur-sm shadow-lg'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="container mx-auto">
        <div className="flex justify-between items-center py-4">
          <motion.div
            className="text-2xl font-bold text-[#FFC400]"
            whileHover={{ scale: 1.05 }}
          >
            <Link href="/">
              <Image
                src="/images/18logo.png"
                alt="18Bricks"
                width={60}
                height={60}
                className='w-20 h-20'
              />
            </Link>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8 items-center">
            {[
              { label: 'Landlord', href: '/landlord' },
              { label: 'Guest', href: 'https://book.18bricksholidayhomes.ae/en' },
              { label: 'Furnishing Packages', href: '/furnishing-packages' },
            ].map((item) => (
              <motion.a
                key={item.label}
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : '_self'}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className={`text-base font-medium transition-colors ${(!scrolled && !isListingsPage && !isDetailsPage && !isServicesPage)
                  ? 'text-white'
                  : 'text-gray-800'
                  }`}
                whileHover={{ y: -2 }}
              >
                {item.label}
              </motion.a>
            ))}

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.button
                  className={`flex items-center gap-1 text-base font-medium transition-colors hover:text-[#FFC400] ${(!scrolled && !isListingsPage && !isDetailsPage && !isServicesPage) ? 'text-white' : 'text-gray-800'
                    }`}
                  whileHover={{ y: -2 }}
                >
                  More
                  <ChevronDown size={16} />
                </motion.button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="start">
                <DropdownMenuItem asChild>
                  <Link href="/services" className="w-full">
                    Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/about" className="w-full">
                    About Us
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/corporate" className="w-full">
                    Corporate & Groups
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/contact" className="w-full">
                    Contact Us
                  </Link>
                </DropdownMenuItem>
                {/* <DropdownMenuItem asChild>
                  <Link href="/careers" className="w-full">
                    Careers
                  </Link>
                </DropdownMenuItem> */}
                <DropdownMenuItem asChild>
                  <Link href="/privacy" className="w-full">
                    Privacy Policy
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/terms" className="w-full">
                    Terms & Conditions
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isHostAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='text-gray-800 border-none'>
                    <User size={18} />
                    Hi, {user.name}
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48" align="end">
                  <DropdownMenuItem asChild>
                    <Link href="/admin" className="w-full flex items-center gap-2">
                      <User size={16} />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 text-red-600 hover:text-red-700"
                  >
                    <X size={16} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className='flex items-center gap-2'>
                <Button size='sm' variant='outline' className='text-gray-800 border-none'>
                  <Link href="/login">
                    Login
                  </Link>
                </Button>
                <Button size='sm' onClick={handleHostButtonClick}>
                  Become a host
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          className="md:hidden bg-white shadow-lg"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <div className="px-4 py-2 space-y-2">
            {[
              { label: 'Landlord', href: '/landlord' },
              { label: 'Guest', href: 'https://book.18bricksholidayhomes.ae/en' },
              { label: 'Furnishing Packages', href: '/furnishing-packages' },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : '_self'}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="block py-2 text-sm font-medium text-gray-800 hover:text-[#FFC400]"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            {/* More section in mobile */}
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                More
              </div>
              {[
                { name: 'About Us', href: '/about' },
                { name: 'Corporate & Groups', href: '/corporate' },
                { name: 'Contact Us', href: '/contact' },
                { name: 'Careers', href: '/careers' }
              ].map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block py-2 pl-4 text-sm font-medium text-gray-800 hover:text-[#FFC400] cursor-pointer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* User section in mobile */}
            {isHostAuthenticated && user && (
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Account
                </div>
                <div className="pl-4 space-y-2">
                  <div className="text-sm font-medium text-gray-800">
                    Hi, {user.name}
                  </div>
                  <Link
                    href="/admin"
                    className="block py-2 text-sm font-medium text-gray-800 hover:text-[#FFC400] cursor-pointer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block py-2 text-sm font-medium text-red-600 hover:text-red-700 cursor-pointer w-full text-left"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      <Dialog open={isHostDialogOpen} onOpenChange={handleHostDialogChange}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              {[0, 1, 2].map((step) => (
                <div key={step} className="flex items-center">
                  <motion.div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${hostStep >= step
                      ? 'bg-[#FFC400] text-black shadow-lg'
                      : 'bg-gray-200 text-gray-500'
                      }`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: step * 0.1 }}
                  >
                    {step + 1}
                  </motion.div>
                  {step < 2 && (
                    <motion.div
                      className={`w-12 h-0.5 mx-2 transition-all duration-300 ${hostStep > step ? 'bg-[#FFC400]' : 'bg-gray-200'
                        }`}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: (step + 0.5) * 0.1 }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogHeader>
            <DialogTitle className="text-center">
              {hostStep === 0 ? 'Choose Your Service' :
                hostStep === 1 ? '' : hostStep === 2 ? 'Host Login & OTP' : ''}
            </DialogTitle>
            <DialogDescription className="text-center">
              {hostStep === 0
                ? 'Select what you would like to offer as a host.'
                : hostStep === 1
                  ? ''
                  : 'Verify yourself with a quick OTP to continue as a host.'}
            </DialogDescription>
          </DialogHeader>

          {hostStep === 0 && (
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Service Type Selection */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Property Option */}
                <motion.button
                  className={`group relative overflow-hidden rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${selectedOption === 'property'
                    ? 'border-[#FFC400] shadow-lg shadow-[#FFC400]/20'
                    : 'border-gray-200 hover:border-[#FFC400]/60'
                    }`}
                  onClick={() => {
                    setSelectedOption('property');
                    setHostFlowError('');
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Background */}
                  <Image src="/images/offer_property.png" alt="Property" width={600} height={600} className='w-full h-full object-contain' />

                  {/* Selection Indicator */}
                  {selectedOption === 'property' && (
                    <motion.div
                      className="absolute top-3 right-3 w-6 h-6 bg-[#FFC400] rounded-full flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  )}

                  {/* Hover Effect Border */}
                  {/* <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-[#FFC400]/40 transition-colors duration-300" /> */}
                </motion.button>

                {/* Service Option */}
                <motion.button
                  className={`group relative overflow-hidden rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${selectedOption === 'service'
                    ? 'border-[#FFC400] shadow-lg shadow-[#FFC400]/20'
                    : 'border-gray-200 hover:border-[#FFC400]/60'
                    }`}
                  onClick={() => {
                    setSelectedOption('service');
                    setHostFlowError('');
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Background */}
                  <Image src="/images/offer_services.png" alt="Property" width={600} height={600} className='w-full h-full object-contain' />

                  {/* Selection Indicator */}
                  {selectedOption === 'service' && (
                    <motion.div
                      className="absolute top-3 right-3 w-6 h-6 bg-[#FFC400] rounded-full flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  )}

                  {/* Hover Effect Border */}
                  {/* <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-[#FFC400]/40 transition-colors duration-300" /> */}
                </motion.button>
              </div>

              {/* Selection Summary */}
              {selectedOption && (
                <motion.div
                  className="bg-[#FFC400]/10 border border-[#FFC400]/30 rounded-lg p-3"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#FFC400] rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 0001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Selected: <span className="text-[#FFC400] font-semibold capitalize">{selectedOption}</span>
                      </p>
                      <p className="text-xs text-gray-600">
                        {selectedOption === 'property' ? 'Continue to select your property type' : 'Continue to set up your service'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              {hostFlowError && (
                <motion.div
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 text-red-500 mt-0.5">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-red-700 font-medium">{hostFlowError}</p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  onClick={() => {
                    if (!selectedOption) {
                      setHostFlowError('Please select either Property or Service to continue.');
                      return;
                    }
                    setHostFlowError('');
                    setHostStep(1);
                  }}
                  disabled={!selectedOption}
                  className="w-full sm:w-auto bg-[#FFC400] hover:bg-[#FFC400]/90 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Button>
              </DialogFooter>
            </motion.div>
          )}

          {hostStep === 1 && selectedOption === 'property' && (
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Property Type Header */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Choose Property Type</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Select the type of property you want to list. This helps us match you with the right guests and provide better service.
                </p>
              </div>

              {/* Property Type Grid */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {propertyOptions.map((option, idx) => (
                  <motion.button
                    key={idx}
                    className={`group relative overflow-hidden rounded-xl border-2 p-0 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${selectedPropertyType === option.name
                      ? 'border-[#FFC400] shadow-lg shadow-[#FFC400]/20'
                      : 'border-gray-200 hover:border-[#FFC400]/60'
                      }`}
                    onClick={() => {
                      setSelectedPropertyType(option.name);
                      setHostFlowError('');
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Background Image */}
                    <div
                      className="h-40 bg-cover bg-center bg-no-repeat"
                      style={{
                        backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%), url(${option.logo || '/images/placeholder-property.jpg'})`
                      }}
                    />

                    {/* Content Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Selection Indicator */}
                    {selectedPropertyType === option.name && (
                      <motion.div
                        className="absolute top-3 right-3 w-6 h-6 bg-[#FFC400] rounded-full flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </motion.div>
                    )}

                    {/* Text Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg mb-1 group-hover:text-[#FFC400] transition-colors">
                        {option.name}
                      </h3>
                      <p className="text-sm text-gray-200 line-clamp-2 opacity-90 group-hover:opacity-100 transition-opacity">
                        {option.description || `${option.name} property for rent`}
                      </p>
                    </div>

                    {/* Hover Effect Border */}
                    <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-[#FFC400]/40 transition-colors duration-300" />
                  </motion.button>
                ))}
              </div>

              {/* Selection Summary */}
              {selectedPropertyType && (
                <motion.div
                  className="bg-[#FFC400]/10 border border-[#FFC400]/30 rounded-lg p-3"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#FFC400] rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Selected: <span className="text-[#FFC400] font-semibold">{selectedPropertyType}</span>
                      </p>
                      <p className="text-xs text-gray-600">Ready to continue with your property listing</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              {hostFlowError && (
                <motion.div
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 text-red-500 mt-0.5">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-red-700 font-medium">{hostFlowError}</p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  onClick={handlePropertyTypeContinue}
                  disabled={!selectedPropertyType}
                  className="w-full sm:w-auto bg-[#FFC400] hover:bg-[#FFC400]/90 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Button>
              </DialogFooter>
            </motion.div>
          )}

          {hostStep === 1 && selectedOption === 'service' && (
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-[#FFC400] rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Service Provider Setup</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Ready to offer your services? Continue to create your service profile and start connecting with customers.
                </p>
              </div>

              {/* Error Message */}
              {hostFlowError && (
                <motion.div
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 text-red-500 mt-0.5">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-red-700 font-medium">{hostFlowError}</p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Button
                  variant="ghost"
                  onClick={() => setHostStep(0)}
                  className="transition-all duration-200 hover:bg-gray-100"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button
                  onClick={handleServiceContinue}
                  className="bg-[#FFC400] hover:bg-[#FFC400]/90 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200"
                >
                  <span className="flex items-center gap-2">
                    Continue to Service Setup
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Button>
              </DialogFooter>
            </motion.div>
          )}

          {hostStep === 2 && (
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Authentication Header */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FFC400] to-[#FFB000] rounded-full flex items-center justify-center mx-auto shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedOption === 'property' ? 'Property Host Account' : 'Service Provider Account'}
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {selectedOption === 'property'
                    ? 'Create or sign in to your account to start listing your property'
                    : 'Create or sign in to your account to start offering your services'
                  }
                </p>
              </div>

              {/* Authentication Card */}
              <motion.div
                className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Tabs value={hostAuthMode} onValueChange={(value) => {
                  setHostAuthMode(value as 'signin' | 'signup');
                  setHostFlowError('');
                }} className="w-full">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <TabsList className="grid w-full grid-cols-2 h-12 bg-white border border-gray-200 rounded-lg p-1">
                      <TabsTrigger
                        value="signin"
                        className="text-sm font-medium data-[state=active]:bg-[#FFC400] data-[state=active]:text-black data-[state=active]:shadow-md rounded-md transition-all duration-200"
                      >
                        Sign In
                      </TabsTrigger>
                      <TabsTrigger
                        value="signup"
                        className="text-sm font-medium data-[state=active]:bg-[#FFC400] data-[state=active]:text-black data-[state=active]:shadow-md rounded-md transition-all duration-200"
                      >
                        Sign Up
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {/* Sign In Tab */}
                  <TabsContent value="signin" className="mt-0">
                    <motion.div
                      className="p-6 space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="signin-email" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address
                        </Label>
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="your@email.com"
                          value={hostEmail}
                          onChange={(e) => {
                            setHostEmail(e.target.value);
                            if (hostFlowError) setHostFlowError('');
                          }}
                          className="transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signin-password" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="signin-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={hostPassword}
                            onChange={(e) => {
                              setHostPassword(e.target.value);
                              if (hostFlowError) setHostFlowError('');
                            }}
                            className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </TabsContent>

                  {/* Sign Up Tab */}
                  <TabsContent value="signup" className="mt-0">
                    <motion.div
                      className="p-6 space-y-6"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="signup-name" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                            <UserIcon className="w-4 h-4" />
                            Full Name *
                          </Label>
                          <Input
                            id="signup-name"
                            type="text"
                            placeholder="John Doe"
                            value={hostName}
                            onChange={(e) => {
                              setHostName(e.target.value);
                              if (hostFlowError) setHostFlowError('');
                            }}
                            className="transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="signup-mobile" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                            📱 Mobile Number *
                          </Label>
                          <Input
                            id="signup-mobile"
                            type="tel"
                            placeholder="+9715XXXXXXXX"
                            value={hostMobile}
                            onChange={(e) => {
                              setHostMobile(e.target.value);
                              if (hostFlowError) setHostFlowError('');
                            }}
                            className="transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-email" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address *
                        </Label>
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="your@email.com"
                          value={hostEmail}
                          onChange={(e) => {
                            setHostEmail(e.target.value);
                            if (hostFlowError) setHostFlowError('');
                          }}
                          className="transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-password" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Password *
                        </Label>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a password (min 6 characters)"
                            value={hostPassword}
                            onChange={(e) => {
                              setHostPassword(e.target.value);
                              if (hostFlowError) setHostFlowError('');
                            }}
                            className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-confirm-password" className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Confirm Password *
                        </Label>
                        <div className="relative">
                          <Input
                            id="signup-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            value={hostConfirmPassword}
                            onChange={(e) => {
                              setHostConfirmPassword(e.target.value);
                              if (hostFlowError) setHostFlowError('');
                            }}
                            className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-[#FFC400]/20"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </TabsContent>
                </Tabs>
              </motion.div>

              {/* Error Message */}
              {hostFlowError && (
                <motion.div
                  className={`rounded-lg border px-4 py-3 ${hostFlowError.includes('successful')
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                    }`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 mt-0.5 ${hostFlowError.includes('successful')
                      ? 'text-green-500'
                      : 'text-red-500'
                      }`}>
                      {hostFlowError.includes('successful') ? (
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <p className={`text-sm font-medium ${hostFlowError.includes('successful')
                      ? 'text-green-700'
                      : 'text-red-700'
                      }`}>
                      {hostFlowError}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-between bg-gray-50 px-6 py-4 border-t border-gray-200">
                <Button
                  variant="ghost"
                  onClick={() => setHostStep(1)}
                  className="transition-all duration-200 hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button
                  onClick={hostAuthMode === 'signin' ? handleHostSignIn : handleHostSignUp}
                  disabled={isLoading || (hostAuthMode === 'signin' ? (!hostEmail.trim() || !hostPassword.trim()) : (!hostName.trim() || !hostEmail.trim() || !hostMobile.trim() || !hostPassword.trim() || !hostConfirmPassword.trim()))}
                  className="bg-[#FFC400] hover:bg-[#FFC400]/90 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      {hostAuthMode === 'signin' ? 'Sign In & Continue' : 'Create Account'}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </Button>
              </DialogFooter>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>
    </motion.nav>
  );
}
